import { useState } from 'react';
import { Loader2, Tv } from 'lucide-react';
import { toast } from 'sonner';
import { Button, Label, RadioGroup, RadioGroupItem } from '@staff-app/ui';
import { errorText, hdmiCecValue } from '../lib/device-commands';
import { TestingDialogFrame, type TestingCommandDialogProps } from './TestingDialogFrame';

/**
 * More › Send HDMI CEC — port of scio3 `HdmiCecDialog`: addCommandExecution MDM_HDMI_CEC with
 * 'cecHDMIOn' | 'cecHDMIOff'.
 */
export function TestingHdmiCecDialog({ open, commands, onClose, onDone }: TestingCommandDialogProps) {
  const [action, setAction] = useState<'on' | 'off'>('on');
  const [sending, setSending] = useState(false);

  const send = async () => {
    setSending(true);
    try {
      await commands.addCommandExecution('MDM_HDMI_CEC', hdmiCecValue(action));
      toast.success('CEC command sent');
      onDone();
      onClose();
    } catch (err) {
      toast.error(errorText(err, 'Failed to send CEC command'));
    } finally {
      setSending(false);
    }
  };

  return (
    <TestingDialogFrame
      open={open}
      onClose={onClose}
      title={'Send HDMI CEC'}
      icon={Tv}
      testId="testing-hdmi-cec-dialog"
      footer={
        <Button onClick={() => void send()} disabled={sending} data-testid="testing-hdmi-cec-send">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
          {'Send'}
        </Button>
      }
    >
      <p className="text-sm text-muted-foreground">
        {'Turn the connected display on or off over HDMI CEC.'}
      </p>
      <RadioGroup value={action} onValueChange={(v) => setAction(v === 'off' ? 'off' : 'on')}>
        <div className="flex items-center gap-2">
          <RadioGroupItem value="on" id="testing-cec-on" aria-label={'Display on'} />
          <Label htmlFor="testing-cec-on" className="cursor-pointer text-sm font-normal">
            {'Display on'}
          </Label>
        </div>
        <div className="flex items-center gap-2">
          <RadioGroupItem value="off" id="testing-cec-off" aria-label={'Display off'} />
          <Label htmlFor="testing-cec-off" className="cursor-pointer text-sm font-normal">
            {'Display off'}
          </Label>
        </div>
      </RadioGroup>
    </TestingDialogFrame>
  );
}
